import http from "./http.service";

/**
 * La mensajería entre comprador y vendedor.
 *
 * <p>Además de envolver las llamadas, mantiene el contador de no leídos que pinta el globo del
 * header. Vive acá y no en el componente porque lo miran dos lugares distintos —el ícono del navbar
 * y las solapas de la bandeja— y hacer que cada uno lo pida por su cuenta duplicaría el request en
 * cada render.
 */

export type ChatTipo = "CONSULTA" | "PEDIDO";

class ChatService {

    /** Total y desglose por solapa. Arranca en cero para no pintar un globo antes de saber. */
    noLeidos: { TOTAL: number; CONSULTA: number; PEDIDO: number } = {
        TOTAL: 0,
        CONSULTA: 0,
        PEDIDO: 0,
    };

    private suscriptores: ((n: any) => void)[] = [];

    /** Avisa cuando cambian los no leídos. Devuelve cómo dejar de escuchar. */
    onNoLeidos = (cb: (n: any) => void): (() => void) => {
        this.suscriptores.push(cb);
        cb(this.noLeidos);
        return () => {
            this.suscriptores = this.suscriptores.filter((s) => s !== cb);
        };
    };

    private emitir = () => this.suscriptores.forEach((s) => s(this.noLeidos));

    refrescarNoLeidos = () =>
        http.get("/api/chat/no-leidos").then(
            (data: any) => {
                const porTipo = data?.porTipo || {};
                this.noLeidos = {
                    TOTAL: Number(data?.noLeidos || 0),
                    CONSULTA: Number(porTipo.CONSULTA || 0),
                    PEDIDO: Number(porTipo.PEDIDO || 0),
                };
                this.emitir();
                return this.noLeidos;
            },
            () => this.noLeidos
        );

    /**
     * Suma uno al contador sin ir al servidor.
     *
     * <p>Lo llama el handler del WebSocket cuando llega un mensaje: el globo tiene que moverse en el
     * momento, y volver a pedir el conteo por cada mensaje que entra convierte una conversación
     * animada en una ráfaga de requests.
     */
    sumarNoLeido = (tipo: ChatTipo) => {
        this.noLeidos = {
            ...this.noLeidos,
            TOTAL: this.noLeidos.TOTAL + 1,
            [tipo]: (this.noLeidos as any)[tipo] + 1,
        } as any;
        this.emitir();
    };

    reset = () => {
        this.noLeidos = { TOTAL: 0, CONSULTA: 0, PEDIDO: 0 };
        this.emitir();
    };

    /** La bandeja. `tipo` vacío trae las dos solapas juntas. */
    bandeja = (params: { tipo?: string; q?: string; soloNoLeidas?: boolean; page?: number; size?: number }) => {
        const qs = new URLSearchParams();
        if (params.tipo) qs.set("tipo", params.tipo);
        if (params.q) qs.set("q", params.q);
        if (params.soloNoLeidas) qs.set("soloNoLeidas", "true");
        qs.set("page", String(params.page ?? 0));
        qs.set("size", String(params.size ?? 30));

        return http.get(`/api/chat/conversaciones?${qs.toString()}`);
    };

    hilo = (id: number) => http.get(`/api/chat/conversaciones/${id}`);

    /** Abre la consulta por una publicación, o devuelve la que ya existía. */
    abrirPorProducto = (productoId: number) =>
        http.post("/api/chat/conversaciones", { productoId });

    /** Abre el hilo de postventa de una compra, o devuelve el que ya existía. */
    abrirPorVenta = (ventaId: number) => http.post("/api/chat/conversaciones", { ventaId });

    enviar = (conversacionId: number, texto: string) =>
        http.post(`/api/chat/conversaciones/${conversacionId}/mensajes`, { texto });

    cerrar = (conversacionId: number) =>
        http.post(`/api/chat/conversaciones/${conversacionId}/cerrar`, {});
}

const chatService = new ChatService();

export default chatService;
